import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSEO, getOrganizationSchema, getBreadcrumbSchema } from '../../utils/seo';
import SiteFooter from '../../components/feature/SiteFooter';
import SiteNav from '../../components/feature/SiteNav';

const GRADIENT = 'linear-gradient(135deg, #14b8a6 0%, #10b981 100%)';

const HERO_IMAGE = 'https://images.pexels.com/photos/8667538/pexels-photo-8667538.jpeg?auto=compress&w=2400&q=80';

const trackHotelCta = (ctaType: string, ctaLocation: string) => {
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({
    event: 'cta_click',
    cta_type: ctaType,
    cta_location: ctaLocation,
  });
};

const RATE_MARKERS = [
  { pos: 0, rate: 0, label: 'Direktbuchung', color: '#10b981' },
  { pos: 8, rate: 1.5, label: 'Google Hotel Ads (Klick)', color: '#84cc16' },
  { pos: 20, rate: 4, label: 'Eigene Partnerschaften', color: '#a3e635' },
  { pos: 40, rate: 8, label: 'Vrbo', color: '#fbbf24' },
  { pos: 60, rate: 14, label: 'Booking.com', color: '#fb923c' },
  { pos: 74, rate: 17, label: 'Airbnb / HRS', color: '#f87171' },
  { pos: 100, rate: 22, label: 'Expedia (Max)', color: '#ef4444' },
];

const posToRate = (pos: number): number => {
  for (let i = 1; i < RATE_MARKERS.length; i++) {
    if (pos <= RATE_MARKERS[i].pos) {
      const m1 = RATE_MARKERS[i - 1];
      const m2 = RATE_MARKERS[i];
      const ratio = (pos - m1.pos) / (m2.pos - m1.pos);
      return m1.rate + ratio * (m2.rate - m1.rate);
    }
  }
  return 22;
};

const formatEur = (n: number) => n.toLocaleString('de-DE') + ' €';

// ─── Scroll-Build Gebäude-Sektion (Pinned Choreografie) ───
const BAUSTEINE_CHAPTERS = [
  {
    step: 1,
    num: '01 · Lobby · Fundament',
    titlePlain: 'Website',
    titlePre: 'Hotel-',
    text: 'Ohne festes Erdgeschoss wackelt alles, was darüber kommt. Die Website ist Ihr Fundament – sie trägt jede Direktbuchung, die nicht über ein Portal läuft und keine Provision kostet.',
    list: [
      'Mobile-first – 70 % aller Hotel-Buchungen passieren am Smartphone',
      'Texte vom Werbetexter, gebaut vom Entwickler – beides aus einer Hand',
      'Native Buchungstool-Integration statt iframe-Notlösung',
      'Quellcode gehört Ihnen, kein Knebelvertrag',
    ],
    boost: false,
  },
  {
    step: 2,
    num: '02 · Zimmer · Buchung',
    titlePlain: 'Buchungstool',
    titlePre: 'Das passende ',
    text: 'Steht das Fundament, kommen die Zimmer. Welches Buchungssystem zu Ihrem Haus passt, hängt von Größe, Portal-Mix und Personal ab. Wir beraten ehrlich – ohne Provision von einem Anbieter und damit ohne Verkaufsdruck.',
    list: [
      'DIRS21, Lodgify, Smoobu, Bookassist, easybooking, Cultuzz',
      'Beratung, welches System wirklich zu Ihrem Haus passt',
      'Komplette Einrichtung und native Integration in die Website',
      'Auch passend, wenn Sie schon ein Tool im Einsatz haben',
    ],
    boost: false,
  },
  {
    step: 3,
    num: '03 · Dach · Sichtbarkeit',
    titlePlain: 'Boost',
    titlePre: 'Der ',
    text: 'Website steht, Tool läuft – und trotzdem kommen die Direktbuchungen schleppend? Dann fehlt das Dach: Sichtbarkeit. Wir sorgen dafür, dass Ihr Haus dort auftaucht, wo Gäste suchen – und direkt bei Ihnen buchen.',
    list: [
      'Lokale SEO und strukturierte Daten für Hotels',
      'Google Hotel Ads und kostenlose Google-Buchungslinks',
      'Buchungsstrecken-Audit gegen Abbruchquoten',
      'Klare Absprachen statt undurchsichtigem Stundenpaket',
    ],
    boost: false,
  },
];

function HotelSVG({ f2, f3 }: { f2: number; f3: number }) {
  // f2, f3: 0..1 Fortschritt pro Geschoss (scroll-gekoppelt, kein CSS-Transition)
  const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
  const e2 = easeOut(f2);
  const e3 = easeOut(f3);
  const y2 = -140 * (1 - e2); // 1.OG: fällt von -140 auf 0
  const y3 = -160 * (1 - e3); // Dach: fällt von -160 auf 0
  return (
    <svg viewBox="0 0 460 580" className="w-full h-auto block overflow-visible" aria-label="Hotel baut sich auf">
      <defs>
        <linearGradient id="roof-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#14b8a6" />
          <stop offset="1" stopColor="#0d9488" />
        </linearGradient>
      </defs>
      <line x1="20" y1="540" x2="440" y2="540" stroke="#1c1917" strokeWidth="2" />
      <ellipse cx="230" cy="544" rx="200" ry="6" fill="rgba(0,0,0,.15)" />

      {/* EG */}
      <g>
        <rect x="60" y="400" width="340" height="140" fill="#1c1917" stroke="#1c1917" strokeWidth="2" />
        <path d="M 200 540 L 200 466 Q 200 444 230 444 Q 260 444 260 466 L 260 540 Z" fill="#14b8a6" />
        <line x1="230" y1="446" x2="230" y2="540" stroke="white" strokeWidth="1.5" />
        <circle cx="218" cy="498" r="2.5" fill="white" />
        <circle cx="242" cy="498" r="2.5" fill="white" />
        <path d="M 188 446 L 272 446 L 286 430 L 174 430 Z" fill="#0d9488" stroke="white" strokeWidth="1.5" />
        <rect x="80" y="446" width="42" height="58" fill="white" />
        <line x1="101" y1="446" x2="101" y2="504" stroke="#1c1917" strokeWidth="1" />
        <line x1="80" y1="475" x2="122" y2="475" stroke="#1c1917" strokeWidth="1" />
        <rect x="134" y="446" width="42" height="58" fill="white" opacity=".75" />
        <line x1="155" y1="446" x2="155" y2="504" stroke="#1c1917" strokeWidth="1" />
        <line x1="134" y1="475" x2="176" y2="475" stroke="#1c1917" strokeWidth="1" />
        <rect x="284" y="446" width="42" height="58" fill="white" opacity=".75" />
        <line x1="305" y1="446" x2="305" y2="504" stroke="#1c1917" strokeWidth="1" />
        <line x1="284" y1="475" x2="326" y2="475" stroke="#1c1917" strokeWidth="1" />
        <rect x="338" y="446" width="42" height="58" fill="white" />
        <line x1="359" y1="446" x2="359" y2="504" stroke="#1c1917" strokeWidth="1" />
        <line x1="338" y1="475" x2="380" y2="475" stroke="#1c1917" strokeWidth="1" />
        <text x="230" y="423" textAnchor="middle" fontFamily="Outfit" fontWeight="900" fontSize="11" fill="#14b8a6" letterSpacing="6">HOTEL</text>
      </g>

      {/* 1.OG — scroll-gescrubbt */}
      <g style={{ opacity: f2 > 0.02 ? 1 : 0, transform: `translateY(${y2}px)` }}>
        <rect x="60" y="280" width="340" height="120" fill="#f4f1eb" stroke="#1c1917" strokeWidth="2" />
        <rect x="56" y="276" width="348" height="6" fill="#1c1917" />
        {[82, 148, 214, 280, 346].map((x, i) => (
          <g key={i}>
            <rect x={x} y="298" width="46" height="76" fill="#1c1917" />
            <rect x={x + 4} y="302" width="38" height="56" fill="#99f6e4" stroke="#1c1917" strokeWidth="1.2" />
            <line x1={x + 19} y1="302" x2={x + 19} y2="358" stroke="#1c1917" strokeWidth=".8" />
            <line x1={x + 4} y1="330" x2={x + 42} y2="330" stroke="#1c1917" strokeWidth=".8" />
            <rect x={x - 6} y="370" width="58" height="6" fill="#1c1917" />
          </g>
        ))}
      </g>

      {/* Dach — scroll-gescrubbt */}
      <g style={{ opacity: f3 > 0.02 ? 1 : 0, transform: `translateY(${y3}px)` }}>
        <rect x="80" y="200" width="300" height="80" fill="#f4f1eb" stroke="#1c1917" strokeWidth="2" />
        <rect x="76" y="196" width="308" height="6" fill="#1c1917" />
        {[110, 204, 298].map((x, i) => (
          <g key={i}>
            <path d={`M ${x} 270 L ${x} 226 Q ${x} 210 ${x + 16} 210 L ${x + 36} 210 Q ${x + 52} 210 ${x + 52} 226 L ${x + 52} 270 Z`} fill="#99f6e4" stroke="#1c1917" strokeWidth="1.5" />
            <line x1={x + 26} y1="210" x2={x + 26} y2="270" stroke="#1c1917" strokeWidth=".8" />
          </g>
        ))}
        <polygon points="60,200 400,200 230,90" fill="url(#roof-grad)" stroke="#1c1917" strokeWidth="2" />
        <line x1="230" y1="90" x2="230" y2="34" stroke="#1c1917" strokeWidth="2.5" />
        <polygon points="230,38 230,66 280,52" fill="#1c1917" stroke="#1c1917" strokeWidth="1.5" />
      </g>
    </svg>
  );
}

const SUMMARIES = ['Hotel-Website', 'Buchungstool', 'Mehr Direktbuchungen'];

// Gemeinsamer Mobile-Block (für beide Varianten identisch)
function BausteineMobile({ goToBooking }: { goToBooking: (t: string, l: string) => void }) {
  return (
    <section className="py-16 bg-[#faf9f7]">
      <div className="max-w-2xl mx-auto px-4 text-center mb-10">
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-teal-700 mb-3">Drei Schritte, ein System.</p>
        <h2 className="text-3xl font-black text-gray-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}>
          Eine Website. Ein Buchungstool.<br />
          <em className="not-italic" style={{ background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Ein großer Schritt für die Unabhängigkeit.</em>
        </h2>
      </div>
      <div className="max-w-xs mx-auto px-4 mb-10"><HotelSVG f2={1} f3={1} /></div>
      <div className="max-w-xl mx-auto px-4 space-y-10">
        {BAUSTEINE_CHAPTERS.map((ch) => (
          <div key={ch.step}>
            <div className="flex items-center gap-2 mb-2 text-xs font-mono uppercase tracking-wider text-teal-700">
              <span className="text-white px-2 py-1 rounded font-bold" style={{ background: '#14b8a6', fontFamily: "'Outfit', sans-serif" }}>{ch.num.split(' · ')[0]}</span>
              <span>{ch.num.split(' · ').slice(1).join(' · ')}</span>
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}>{ch.titlePre}<span style={{ color: '#14b8a6' }}>{ch.titlePlain}</span></h3>
            <p className="text-base text-gray-600 leading-relaxed mb-4">{ch.text}</p>
            <ul className="space-y-2.5">
              {ch.list.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600 leading-snug">
                  <span className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center mt-0.5 text-white" style={{ background: '#14b8a6', fontSize: '10px' }}>✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
        <div className="text-center pt-4">
          <button onClick={() => goToBooking('hotel_bausteine_gesamt', 'bausteine')} className="inline-flex items-center gap-2 px-7 py-4 rounded-xl text-white font-bold" style={{ background: GRADIENT, boxShadow: '0 8px 24px rgba(20,184,166,.3)' }}>
            Gespräch vereinbaren <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    </section>
  );
}

function useScrollProgress(ref: { current: HTMLDivElement | null }, enabled: boolean) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const onScroll = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      setProgress(total > 0 ? scrolled / total : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [enabled, ref]);
  return progress;
}

// ═══════ BAUSTEINE: Gebäude-Choreografie mit Pfeil ═══════
function BausteineB({ goToBooking }: { goToBooking: (t: string, l: string) => void }) {
  const [isDesktop, setIsDesktop] = useState(false);
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 768);
    check(); window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  const p = useScrollProgress(sectionRef, isDesktop);
  const clamp01 = (x: number) => Math.min(Math.max(x, 0), 1);
  // Phasen-Grenzen: jedes Geschoss landet FRÜH in seiner Phase, danach bleibt
  // der Text lange neben dem Haus stehen (großes Dwell-Fenster), bevor er beim
  // Weiterscrollen ins kompakte Label kollabiert.
  const f2 = clamp01((p - 0.34) / 0.07);   // 1.OG fällt 0.34–0.41
  const f3 = clamp01((p - 0.62) / 0.07);   // Dach fällt 0.62–0.69
  const activeText = p < 0.08 ? 0 : p < 0.34 ? 1 : p < 0.62 ? 2 : 3;
  const shift = clamp01((p - 0.07) / 0.08);
  const hotelLeftPct = 50 - shift * 24;
  const hotelEnter = clamp01(p / 0.05);
  const collapsed1 = p >= 0.34, collapsed2 = p >= 0.62, collapsed3 = p >= 0.92;
  const endState = p >= 0.92;
  const rowTop: Record<number, number> = { 1: 70, 2: 50, 3: 30 };

  // Sequenz pro Phase: Geschoss landet -> Pfeil -> Text. Pfeil und Text erscheinen
  // früh; der gesamte Rest der Phase ist reine Standzeit des Textes.
  const arrowStart = activeText === 1 ? 0.10 : activeText === 2 ? 0.42 : 0.70;
  const arrowEnter = clamp01((p - arrowStart) / 0.03);
  const textStart = activeText === 1 ? 0.14 : activeText === 2 ? 0.46 : 0.74;
  const textEnter = clamp01((p - textStart) / 0.05);

  const ch = activeText >= 1 ? BAUSTEINE_CHAPTERS[activeText - 1] : null;
  // Kollaps-Choreografie: in den letzten 0.08 vor jeder Phasengrenze schrumpft der
  // Langtext scroll-gescrubbt zur kompakten 01/02/03-Überschrift zusammen, sodass er
  // nahtlos in das stehenbleibende Label übergeht (kein hartes Umschalten).
  const boundaryOf: Record<number, number> = { 1: 0.34, 2: 0.62, 3: 0.92 };
  const collapseProgress = activeText >= 1 ? clamp01((p - (boundaryOf[activeText] - 0.08)) / 0.08) : 0;
  const showActiveLong = activeText >= 1 && p < boundaryOf[activeText];

  if (!isDesktop) return <BausteineMobile goToBooking={goToBooking} />;

  return (
    <section ref={sectionRef} className="bg-[#faf9f7]" style={{ height: '600vh', position: 'relative' }}>
      <div style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden' }}>
        <div className="max-w-3xl mx-auto px-6 text-center pt-16" style={{ opacity: Math.max(0, 1 - p / 0.1), transition: 'opacity .3s' }}>
          <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-teal-700 mb-4">Drei Schritte, ein System.</p>
          <h2 className="text-3xl sm:text-5xl font-black text-gray-900 mb-5" style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}>
            Eine Website. Ein Buchungstool.<br /><em className="not-italic" style={{ background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Ein großer Schritt für die Unabhängigkeit.</em>
          </h2>
        </div>

        {/* Hotel */}
        <div style={{ position: 'absolute', top: '50%', left: `${hotelLeftPct}%`, transform: 'translate(-50%, -50%)', width: 'min(40vw, 420px)', opacity: hotelEnter, transition: 'left .85s cubic-bezier(.22,1,.36,1)' }}>
          <HotelSVG f2={f2} f3={f3} />
        </div>

        {/* Pfeil zwischen Haus und Text — erscheint nach Geschoss-Ankunft */}
        {showActiveLong && (
          <div style={{ position: 'absolute', left: '47%', top: `${rowTop[activeText]}%`, transform: `translateY(-50%) scale(${0.6 + arrowEnter * 0.4})`, width: 'min(5vw, 64px)', opacity: arrowEnter * (1 - collapseProgress), transition: 'top .5s cubic-bezier(.22,1,.36,1)' }}>
            <svg viewBox="0 0 90 40" style={{ width: '100%' }}><path d="M 0 14 L 60 14 L 60 4 L 90 20 L 60 36 L 60 26 L 0 26 Z" fill="#14b8a6" /></svg>
          </div>
        )}

        {/* Aktiver Langtext — erscheint nach dem Pfeil und kollabiert scroll-gescrubbt
            zur kompakten 0X-Überschrift: Untertitel + Fließtext geben ihre Höhe ab,
            der Langtitel schrumpft und blendet in die kurze Zusammenfassung über. */}
        {showActiveLong && ch && (
          <div style={{ position: 'absolute', left: '54%', top: `${rowTop[activeText]}%`, transform: `translateY(-50%) translateX(${(1 - textEnter) * 40}px)`, width: 'min(38vw, 440px)', opacity: textEnter }}>
            {/* Mono-Untertitel — blendet aus und gibt seine Höhe ab */}
            <div className="font-mono uppercase tracking-wider text-teal-700" style={{ fontSize: '0.75rem', opacity: 1 - collapseProgress, height: `${(1 - collapseProgress) * 1.5}rem`, marginBottom: `${(1 - collapseProgress) * 0.5}rem`, overflow: 'hidden' }}>
              {ch.num.split(' · ').slice(1).join(' · ')}
            </div>
            {/* Kopfzeile: Nummer + Titel — bleibt als kompakte 0X-Überschrift stehen */}
            <div className="flex items-center gap-3">
              <span className="text-white px-2.5 py-1 rounded font-bold text-sm flex-shrink-0" style={{ background: '#14b8a6', fontFamily: "'Outfit', sans-serif" }}>{ch.num.split(' · ')[0]}</span>
              <div style={{ position: 'relative', flex: 1, minHeight: '2rem', display: 'flex', alignItems: 'center' }}>
                {/* Langtitel — schrumpft und verblasst */}
                <h3 className="font-black text-gray-900" style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.1, fontSize: 'clamp(1.5rem, 2.5vw, 2.25rem)', transformOrigin: 'left center', transform: `scale(${1 - 0.33 * collapseProgress})`, opacity: 1 - collapseProgress, margin: 0 }}>{ch.titlePre}<span style={{ color: '#14b8a6' }}>{ch.titlePlain}</span></h3>
                {/* Kompakte Zusammenfassung — blendet ein */}
                <span className="text-xl lg:text-2xl font-black text-gray-900" style={{ position: 'absolute', left: 0, fontFamily: "'Outfit', sans-serif", whiteSpace: 'nowrap', opacity: collapseProgress, pointerEvents: 'none' }}>{SUMMARIES[activeText - 1]}</span>
              </div>
            </div>
            {/* Fließtext — kollabiert nach oben weg */}
            <p className="text-base text-gray-600 leading-relaxed" style={{ opacity: Math.max(0, 1 - collapseProgress * 1.6), maxHeight: `${(1 - collapseProgress) * 12}rem`, marginTop: `${(1 - collapseProgress) * 1}rem`, transform: `translateY(${-collapseProgress * 10}px)`, overflow: 'hidden' }}>{ch.text}</p>
          </div>
        )}

        {/* Endzustand-Übersicht: kollabierte Labels 01–03, gestaffelt + klickbar */}
        {[3, 2, 1].map((s) => {
          const coll = s === 1 ? collapsed1 : s === 2 ? collapsed2 : collapsed3;
          if (!coll) return null;
          return (
            <button key={'lbl' + s} onClick={endState ? () => setExpandedStep(expandedStep === s ? null : s) : undefined}
              style={{ position: 'absolute', left: '54%', top: `${rowTop[s]}%`, transform: 'translateY(-50%)', cursor: endState ? 'pointer' : 'default' }}
              className="flex items-center gap-3 group text-left bg-transparent border-0">
              <span className="text-white px-2.5 py-1 rounded font-bold text-sm flex-shrink-0" style={{ background: '#14b8a6', fontFamily: "'Outfit', sans-serif" }}>0{s}</span>
              <span className="text-xl lg:text-2xl font-black text-gray-900 group-hover:text-teal-700 transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>{SUMMARIES[s - 1]}</span>
              {endState && <i className="ri-add-line text-teal-600 text-lg opacity-50 group-hover:opacity-100"></i>}
            </button>
          );
        })}

        {/* Pfeile im Endzustand neben jedem Label */}
        {endState && [3, 2, 1].map((s) => (
          <div key={'arr' + s} style={{ position: 'absolute', left: '47%', top: `${rowTop[s]}%`, transform: 'translateY(-50%)', width: 'min(5vw, 64px)', animation: 'fadeIn .5s ease' }}>
            <svg viewBox="0 0 90 40" style={{ width: '100%' }}><path d="M 0 14 L 60 14 L 60 4 L 90 20 L 60 36 L 60 26 L 0 26 Z" fill="#14b8a6" opacity="0.55" /></svg>
          </div>
        ))}

        {/* Klick-Overlay */}
        {endState && expandedStep !== null && (
          <div onClick={() => setExpandedStep(null)} style={{ position: 'absolute', inset: 0, background: 'rgba(250,249,247,.93)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20, animation: 'fadeIn .3s ease', cursor: 'pointer' }}>
            <div onClick={(e) => e.stopPropagation()} className="max-w-lg px-8" style={{ cursor: 'default' }}>
              {(() => {
                const c = BAUSTEINE_CHAPTERS[expandedStep - 1];
                return (<>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-teal-700">
                      <span className="text-white px-2 py-1 rounded font-bold" style={{ background: '#14b8a6', fontFamily: "'Outfit', sans-serif" }}>{c.num.split(' · ')[0]}</span>
                      <span>{c.num.split(' · ').slice(1).join(' · ')}</span>
                    </div>
                    <button onClick={() => setExpandedStep(null)} className="text-gray-400 hover:text-gray-700 bg-transparent border-0"><i className="ri-close-line text-2xl"></i></button>
                  </div>
                  <h3 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}>{c.titlePre}<span style={{ color: '#14b8a6' }}>{c.titlePlain}</span></h3>
                  <p className="text-base text-gray-600 leading-relaxed mb-5">{c.text}</p>
                  <ul className="space-y-2.5 mb-6">
                    {c.list.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600 leading-snug">
                        <span className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center mt-0.5 text-white" style={{ background: '#14b8a6', fontSize: '10px' }}>✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => goToBooking(`hotel_baustein_0${expandedStep}`, 'bausteine')} className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-white font-semibold text-sm" style={{ background: GRADIENT }}>
                    Dazu beraten lassen <i className="ri-arrow-right-line"></i>
                  </button>
                </>);
              })()}
            </div>
          </div>
        )}

        {endState && expandedStep === null && (
          <div style={{ position: 'absolute', bottom: '8%', left: '50%', transform: 'translateX(-50%)', animation: 'fadeIn .6s ease' }} className="text-sm text-gray-400 flex items-center gap-2">
            <i className="ri-cursor-line"></i> Bausteine anklicken für Details
          </div>
        )}
      </div>
      <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
    </section>
  );
}





export default function Hotels() {
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://seitenschmied.com';
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [revenue, setRevenue] = useState(15000);
  const [ratePos, setRatePos] = useState(74);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const rateRef = useRef<HTMLInputElement>(null);

  const rate = posToRate(ratePos);
  const monthlyCommission = Math.round((revenue * rate) / 100);
  const annualCommission = monthlyCommission * 12;

  useSEO({
    title: 'Webdesign für Hotels | Mehr Direktbuchungen statt Portal-Provision',
    description: 'Websites für Hotels, Pensionen & B&Bs mit Buchungstool-Integration (DIRS21, Lodgify, Smoobu) und Fokus auf Direktbuchungen. Festpreis, 3–8 Wochen. Kostenloses Erstgespräch.',
    keywords: 'webdesign für hotels, hotel website erstellen, hotel website kosten, direktbuchungen, buchungstool integration, DIRS21, Lodgify, Smoobu',
    canonical: `${siteUrl}/hotels`,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        getOrganizationSchema(),
        getBreadcrumbSchema([
          { name: 'Startseite', url: siteUrl },
          { name: 'Für Hotels', url: `${siteUrl}/hotels` },
        ]),
        {
          '@type': 'Service',
          '@id': `${siteUrl}/hotels#service`,
          name: 'Webdesign für Hotels',
          serviceType: 'Webdesign für Hotels',
          url: `${siteUrl}/hotels`,
          provider: { '@id': `${siteUrl}/#organization` },
          areaServed: { '@type': 'Country', name: 'Deutschland' },
          audience: { '@type': 'Audience', audienceType: 'Hotels, Pensionen, B&Bs' },
          description: 'Hotel-Websites mit Fokus auf Direktbuchungen, Buchungstool-Integration und conversionstarker Copy. Festpreis. 3–8 Wochen Lieferzeit.',
        },
      ],
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const depthFired = { d50: false, d90: false };
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 600);
      // Scroll-Tiefe: einmalig 50 % und 90 % an den dataLayer melden
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      const pct = window.scrollY / max;
      const push = (depth: number) => {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({ event: 'scroll_depth', page: 'hotels', percent: depth });
      };
      if (!depthFired.d50 && pct >= 0.5) { depthFired.d50 = true; push(50); }
      if (!depthFired.d90 && pct >= 0.9) { depthFired.d90 = true; push(90); }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll handler

  const scrollToPakete = () => {
    document.getElementById('pakete')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    trackHotelCta('hotel_pakete_ansehen', 'hero');
  };

  const goToBooking = (ctaType: string, ctaLocation: string) => {
    trackHotelCta(ctaType, ctaLocation);
    navigate('/termin-buchen');
  };

  const nearestMarker = RATE_MARKERS.reduce((closest, m) =>
    Math.abs(m.rate - rate) < Math.abs(closest.rate - rate) ? m : closest
  );

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0" style={{ fontFamily: "'Inter', sans-serif" }}>
      <SiteNav activePage="hotels" navId="hotels-nav" />

      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden bg-[#0e0e0e]">
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMAGE}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/55 to-[#0e0e0e]"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-20 pt-32 lg:pt-40">
          <div className="max-w-3xl">
            <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-6 text-teal-400">Webdesign für Hotels, Pensionen &amp; B&amp;Bs</p>
            <h1 className="text-5xl sm:text-6xl lg:text-8xl font-black text-white mb-6 leading-[0.95]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="block text-lg sm:text-xl lg:text-2xl font-semibold text-teal-300 mb-4 tracking-normal" style={{ fontFamily: "'Inter', sans-serif" }}>Webdesign für Hotels, die mehr Direktbuchungen wollen.</span>
              Check: <span style={{ color: '#14b8a6' }}>in.</span><br />
              Provision: <span style={{ color: '#f87171' }}>out.</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 leading-relaxed mb-10 max-w-xl">
              Hotel-Websites mit passendem Buchungstool, überzeugender Copy und mobiler Buchungsstrecke – damit Gäste direkt bei Ihnen buchen, nicht bei Portalen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={scrollToPakete} className="flex items-center justify-center gap-2 px-7 sm:px-10 py-4 sm:py-5 font-bold text-sm sm:text-base rounded-xl transition-all duration-300 hover:scale-105 text-white" style={{ background: GRADIENT, boxShadow: '0 10px 30px rgba(16,185,129,0.3)' }}>
                <i className="ri-arrow-down-line"></i>Pakete ansehen
              </button>
              <button onClick={() => goToBooking('hotel_erstgespraech', 'hero')} className="flex items-center justify-center gap-2 px-7 sm:px-10 py-4 sm:py-5 font-bold text-sm sm:text-base rounded-xl transition-all duration-300 hover:scale-105 text-white" style={{ backgroundColor: 'rgba(255,255,255,0.12)', border: '2px solid rgba(255,255,255,0.35)', backdropFilter: 'blur(8px)' }}>
                <i className="ri-calendar-line"></i>30 Min. Erstgespräch
              </button>
            </div>
            {/* Trust-Zeile above the fold */}
            <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs sm:text-sm text-gray-400">
              <span className="inline-flex items-center gap-1.5"><i className="ri-plug-line text-teal-400"></i>Buchungstools:</span>
              <span className="font-semibold text-gray-200">DIRS21 · Lodgify · Smoobu</span>
              <span className="hidden sm:inline text-gray-600">|</span>
              <span className="inline-flex items-center gap-1.5"><i className="ri-check-line text-teal-400"></i>Festpreis · 3–8 Wochen · Werbetext inklusive</span>
            </div>
          </div>
        </div>
      </section>

      {/* PILOT — vorerst ausgeblendet, bis Alpenhof-Site live ist */}
      {false && (
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="flex-shrink-0">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-bold text-white mb-3" style={{ background: 'linear-gradient(135deg, #0f4c81, #1a7bbf)' }}>DIRS21</span>
              <h3 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Pilot-Projekt: Alpenhof Gauting</h3>
            </div>
            <p className="text-gray-600 leading-relaxed flex-1">
              Erste vollständige Hotel-Website aus unserer Hand. Mit nativer DIRS21-Integration, mehrsprachiger Struktur und Mobile-first-Buchung. Aktuell in Feinabstimmung – Live-Schaltung in Kürze.
            </p>
          </div>
        </div>
      </section>
      )}

      {/* PROBLEM + ROI */}
      <section className="py-20 sm:py-28" style={{ background: '#0e0e0e' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-teal-400 mb-4">Die Rechnung, die kein Hotelier gerne macht.</p>
          <h2 className="text-3xl sm:text-5xl font-black text-white mb-8" style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}>
            15 bis 22 Prozent Provision.<br />
            <em className="not-italic" style={{ color: '#14b8a6' }}>Pro Buchung. Pro Jahr.</em>
          </h2>
          <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-10">
            Ein Hotel mit zwölf Zimmern, das 50 Prozent seiner Buchungen über{' '}
            <span className="cursor-help underline decoration-dotted underline-offset-4" title="OTA = Online Travel Agency. Buchungsportale wie Booking.com, Expedia oder HRS, die für jede Vermittlung Provision verlangen.">Buchungsportale</span>{' '}
            macht, zahlt zwischen <strong className="text-white">1.500 und 2.500 Euro pro Monat</strong> an Provision. Drei Gründe, warum Hotels diesen Anteil nicht senken:
          </p>

          <div className="grid sm:grid-cols-3 gap-5 mb-12">
            {[
              { num: '1', title: 'Die Website überzeugt nicht.', text: 'Wer auf einer Plattform vergleicht und dann die Hotelseite öffnet, springt zurück – weil sie langsamer lädt oder keine spürbare Buchungsführung hat.' },
              { num: '2', title: 'Die Buchungsmaschine läuft schlecht.', text: 'Oft nachträglich eingebaut, bricht auf dem Smartphone. Wo keines existiert, gehen Anfragen per E-Mail verloren.' },
              { num: '3', title: 'Die Seite wird nicht gefunden.', text: 'Wer nach „Hotel in [Ort] buchen" sucht, landet bei den Portalen. Lokale SEO und strukturierte Daten fehlen.' },
            ].map((item) => (
              <div key={item.num} className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold mb-4" style={{ background: GRADIENT }}>{item.num}</div>
                <h3 className="font-bold text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>

          {/* ROI KALKULATOR */}
          <div className="bg-white/5 rounded-2xl p-6 sm:p-8 border border-white/10">
            <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Wie viel zahlen Sie gerade?</h3>
            <p className="text-sm text-gray-400 mb-8">Bewegen Sie die Regler – wir rechnen live mit.</p>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Monatlicher Umsatz über Buchungsportale</label>
                <input type="range" min={2000} max={50000} step={500} value={revenue} onChange={(e) => setRevenue(Number(e.target.value))} className="w-full accent-teal-500 cursor-pointer" />
                <div className="text-white font-black text-xl mt-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {formatEur(revenue)} <span className="text-sm font-normal text-gray-500">/ Monat</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Durchschnittliche Provisionsrate <span className="text-teal-400">†</span></label>
                <input ref={rateRef} type="range" min={0} max={100} step={0.5} value={ratePos} onChange={(e) => setRatePos(Number(e.target.value))} className="w-full accent-teal-500 cursor-pointer" />
                <div className="text-white font-black text-xl mt-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {rate.toFixed(1)} <span className="text-sm font-normal text-gray-500">%</span>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
                  <span className="text-gray-500">Vergleichbar mit:</span>
                  <span className="px-2 py-1 rounded-md font-semibold" style={{ background: nearestMarker.color + '22', color: nearestMarker.color }}>{nearestMarker.label}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="text-center bg-black/30 rounded-xl p-4">
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Provision / Monat</div>
                <div className="text-2xl font-black text-red-400" style={{ fontFamily: "'Outfit', sans-serif" }}>{formatEur(monthlyCommission)}</div>
              </div>
              <div className="text-center bg-black/30 rounded-xl p-4">
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Provision / Jahr</div>
                <div className="text-2xl font-black text-red-400" style={{ fontFamily: "'Outfit', sans-serif" }}>{formatEur(annualCommission)}</div>
              </div>
            </div>

            <div className="text-xs text-gray-500 space-y-2 mb-6">
              <p><span className="text-teal-400">†</span> <strong className="text-gray-300">Provisionsraten:</strong> Richtwerte (Stand 2025/26). Die genauen Sätze schwanken je nach Vertrag, Programm-Teilnahme und Region.</p>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-6 border-t border-white/10">
              <p className="text-sm text-gray-400 flex-1">Bei einem Direktbuchungsanteil von +20 % würden Sie über {formatEur(Math.round(annualCommission * 0.2))} Provision jährlich einsparen.</p>
              <button onClick={() => goToBooking('hotel_rechnung_besprechen', 'roi-calc')} className="flex items-center gap-2 px-6 py-3 font-bold rounded-xl text-white whitespace-nowrap hover:scale-105 transition-transform" style={{ background: GRADIENT }}>
                <i className="ri-calendar-line"></i>Rechnung besprechen
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* PROVISIONSTABELLE */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-teal-700 mb-4">Marktüberblick</p>
          <h2 className="text-3xl sm:text-5xl font-black text-gray-900 mb-8" style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}>
            Was kosten die <em className="not-italic" style={{ background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Vertriebskanäle</em> wirklich?
          </h2>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-10 max-w-3xl">
            Provisionen, Klick-Kosten und Direktbuchungen im Vergleich – damit Sie sehen, wo Marge bleibt und wo sie verschwindet.
          </p>

          <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-4 font-semibold text-gray-700">Plattform</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Provision</th>
                  <th className="text-left p-4 font-semibold text-gray-700 hidden md:table-cell">Zahlt wer?</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Anmerkungen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { name: 'Booking.com', sub: 'Portal · größte Plattform', rate: '15–18 %', rateColor: 'red', payer: 'Hotel', note: 'Gäste zahlen nichts extra. Genius-Programm kann Kosten durch Rabattdruck erhöhen.' },
                  { name: 'Expedia', sub: 'inkl. Hotels.com, Vrbo', rate: '15–20 %', rateColor: 'red', payer: 'Hotel', note: 'Höchste Portal-Provision. Dafür große Reichweite inkl. Pauschalreisende.' },
                  { name: 'Airbnb', sub: 'Seit Dez. 2025 neues Modell', rate: '15,5 %', rateColor: 'red', payer: 'Hotel / Gastgeber', note: 'Früher Split: 3 % Gastgeber + 14–16 % Gäste. Seit Dez. 2025 einheitlich 15,5 % vom Gastgeber.' },
                  { name: 'HRS', sub: 'Schwerpunkt Geschäftsreisen', rate: '15–17 %', rateColor: 'red', payer: 'Hotel', note: 'Fokus auf Firmenreisende. Für Freizeithotels weniger relevant.' },
                  { name: 'Vrbo / FeWo-direkt', sub: 'Ferienwohnungen', rate: '5–8 %', rateColor: 'orange', payer: 'Hotel + Gäste', note: 'Gastgeber: 5 % + 3 % Transaktionsgebühr. Gäste: 6–15 % Servicegebühr extra.' },
                  { name: 'Google Hotel Ads', sub: 'Metasuche, kein Portal', rate: 'CPC: ~1–3 %', rateColor: 'green', payer: 'Hotel (pro Klick)', note: 'Kein fester Provisionssatz. Zahlung pro Klick, nicht pro Buchung. Buchung läuft direkt über eigene Website.' },
                  { name: 'Google Buchungslinks', sub: 'kostenlos, seit 2021', rate: '0 %', rateColor: 'green', payer: '–', note: 'Kostenlose Sichtbarkeit in der Google-Suche, wenn Buchungssystem angebunden ist. Kein Budget nötig.' },
                  { name: 'Direktbuchung', sub: 'eigene Website', rate: '0 %', rateColor: 'green', payer: '–', note: 'Kosten nur für Buchungssystem (z. B. ibelsa ~63 €/Monat). Höchste Marge pro Buchung.' },
                ].map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4">
                      <div className="font-bold text-gray-900">{row.name}</div>
                      <div className="text-xs text-gray-500 mt-1">{row.sub}</div>
                    </td>
                    <td className="p-4">
                      <span className={`inline-block px-3 py-1 rounded-full font-bold text-xs ${row.rateColor === 'red' ? 'bg-red-50 text-red-700' : row.rateColor === 'orange' ? 'bg-orange-50 text-orange-700' : 'bg-emerald-50 text-emerald-700'}`}>{row.rate}</span>
                    </td>
                    <td className="p-4 text-gray-700 hidden md:table-cell">{row.payer}</td>
                    <td className="p-4 text-gray-600 leading-relaxed">{row.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-4 italic">Alle Angaben sind Richtwerte (Stand 2025/2026). Tatsächliche Provisionen können je nach Vertrag, Programm-Teilnahme und Region abweichen.</p>
        </div>
      </section>

      {/* CASE STUDY — vorerst ausgeblendet, bis Alpenhof-Site live ist */}
      {false && (
      <section className="py-16 sm:py-24" style={{ background: '#0e0e0e' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-teal-400 mb-4">Case Study</p>
          <h2 className="text-3xl sm:text-5xl font-black text-white mb-8" style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}>
            Hotel Alpenhof Gauting:<br />
            <em className="not-italic" style={{ color: '#14b8a6' }}>von Typo3 zur modernen React-Website.</em>
          </h2>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-bold text-red-300 bg-red-900/30 mb-4">Vorher · Typo3 + iframe</span>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>• Schlechte mobile Performance</li>
                <li>• Buchungs-Widget per iframe angeflanscht</li>
                <li>• Regelmäßige Ausfälle</li>
                <li>• Schwer pflegbare Inhalte</li>
              </ul>
            </div>
            <div className="bg-white/5 rounded-2xl p-6 border" style={{ borderColor: '#14b8a6' }}>
              <span className="inline-block px-3 py-1 rounded-full text-xs font-bold text-white mb-4" style={{ background: GRADIENT }}>Nachher · React + DIRS21 nativ</span>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>✓ Mobile-first-Design</li>
                <li>✓ Native DIRS21-Integration</li>
                <li>✓ Mehrsprachige Struktur</li>
                <li>✓ Pflegbar ohne Entwickler</li>
              </ul>
            </div>
          </div>

          <blockquote className="bg-white/5 rounded-2xl p-6 sm:p-8 border-l-4" style={{ borderColor: '#14b8a6' }}>
            <p className="text-base sm:text-lg text-white italic leading-relaxed">
              „Endlich eine Buchungsseite, die auf dem Handy wirklich funktioniert. Das DIRS21-Widget läuft sauber – keine Ausfälle mehr. Und die Texte klingen endlich nach uns."
            </p>
            <p className="text-sm text-gray-400 mt-4">– Inhaberin Hotel Alpenhof Gauting</p>
          </blockquote>
        </div>
      </section>
      )}

      {/* BAUSTEINE - Scroll-Build Gebäude */}
      {/* BAUSTEINE - Gebäude-Animation mit Pfeil */}
      <BausteineB goToBooking={goToBooking} />


      {/* ÜBER SEITENSCHMIED */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-teal-700 mb-4">Über Seitenschmied</p>
          <h2 className="text-3xl sm:text-5xl font-black text-gray-900 mb-12" style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}>
            Bauen Sie auf jemand,<br />
            <em className="not-italic" style={{ background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>der nicht nur Websites baut.</em>
          </h2>

          <div className="grid md:grid-cols-2 gap-10 lg:gap-14 items-start">
            {/* Logo & Marken */}
            <div>
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 sm:p-10 flex items-center justify-center aspect-[4/3]">
                <img
                  src="/images/Seitenschmied_Logo.png"
                  alt="Seitenschmied Logo"
                  className="w-full max-w-[280px] h-auto object-contain"
                />
              </div>

              <p className="text-xs font-semibold text-gray-500 tracking-wider uppercase mt-6 mb-3">Hat u.a. getextet für:</p>
              <div className="flex flex-wrap gap-2">
                {['TUI', 'Hapag-Lloyd', 'Haribo', 'Sky', 'Viessmann', 'Deutsche Bahn'].map((b) => (
                  <span key={b} className="px-3 py-1.5 rounded-md text-sm font-semibold bg-white border border-gray-200 text-gray-700">{b}</span>
                ))}
              </div>
            </div>

            {/* Zitat & Punkte */}
            <div>
              <blockquote className="bg-white rounded-2xl p-6 sm:p-7 border-l-4 mb-6" style={{ borderColor: '#14b8a6' }}>
                <p className="text-base sm:text-lg text-gray-800 italic leading-relaxed mb-5">
                  „Ich finde: Wer ein Hotel führt, das Zimmer stellt, den Service macht und den Gast empfängt, sollte an der eigenen Buchung am meisten verdienen."
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <img
                    src="/images/blasio-hertz.jpg"
                    alt="Blasio Hertz"
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  />
                  <div>
                    <div className="font-bold text-gray-900 text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Blasio Hertz</div>
                    <div className="text-xs text-gray-500">Werbetexter &amp; Webentwickler</div>
                  </div>
                </div>
              </blockquote>

              <div className="space-y-5">
                {[
                  { icon: 'ri-user-line', title: 'Kein Übersetzungsverlust', text: 'Wer schreibt, baut auch. Kein Briefing zwischen Texter und Entwickler, keine Reibung, keine Agenturmarge.' },
                  { icon: 'ri-price-tag-3-line', title: 'Festpreis, kein Nachzahlen', text: 'Auch wenn wir länger brauchen: Ihr Preis ist Ihr Preis. Zahlung gestaffelt – die Hälfte erst nach Ihrer Freigabe.' },
                  { icon: 'ri-tools-line', title: 'Kein Knebelvertrag', text: 'Die Website gehört Ihnen. Quellcode-Übergabe inklusive. Kein Vendor-Lock-in.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center text-white" style={{ background: GRADIENT }}>
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BERATUNG */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-teal-700 mb-4">Beratung</p>
          <h2 className="text-3xl sm:text-5xl font-black text-gray-900 mb-10" style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}>
            Welches Buchungstool passt<br />
            <em className="not-italic" style={{ background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>zu Ihrem Haus?</em>
          </h2>

          <div className="space-y-5">
            {[
              { tool: 'DIRS21', toolColor: 'linear-gradient(135deg, #0f4c81, #1a7bbf)', title: 'DIRS21-Integration für klassische Hotels ab 8 Zimmern', text: 'Marktführer in DACH, robust, mit starker Anbindung an Channel-Manager. Geeignet, wenn das Hotel über mehrere Plattformen verkauft und Tages- oder Saisonpreise differenziert.', link: '/hotels/dirs21', linkLabel: 'Mehr zu DIRS21-Integration' },
              { tool: 'Cultuzz / Bookassist', toolColor: 'linear-gradient(135deg, #5b21b6, #7c3aed)', title: 'Für Hotels mit internationalem Channel-Mix', text: 'Stärker im internationalen Markt, gut bei mehrsprachigen Häusern und Business-Travel.', link: '/hotels/buchungstool-vergleich', linkLabel: 'Im Vergleich ansehen' },
              { tool: 'Lodgify / Smoobu', toolColor: 'linear-gradient(135deg, #047857, #10b981)', title: 'Lodgify- und Smoobu-Websites für kleine Hotels bis 10 Zimmer', text: 'Günstigere Lizenzen, einfacher zu bedienen, gut für Häuser mit überschaubarem Portal-Mix. Smoobu besonders stark für Apartments und Self-Check-in.', link: '/hotels/lodgify', linkLabel: 'Mehr zu Lodgify' },
              { tool: 'Kein Tool', toolColor: '#9ca3af', title: 'Anfragestrecke statt Buchungstool – auch das ist eine Option', text: 'Eine schlanke Anfragestrecke mit DSGVO-konformer Mail-Strecke und automatischer Eingangsbestätigung passt für sehr kleine Häuser. Kostet null monatliche Lizenzgebühr.', link: null, linkLabel: null },
            ].map((item, i) => (
              <div key={i} className="bg-gradient-to-br from-gray-50 to-teal-50 rounded-2xl p-6 border border-teal-100 flex flex-col md:flex-row md:items-start gap-4">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-bold text-white self-start whitespace-nowrap" style={{ background: item.toolColor }}>{item.tool}</span>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-3">{item.text}</p>
                  {item.link && (
                    <a href={item.link} onClick={() => trackHotelCta('hotel_tool_detail_' + item.tool.toLowerCase().replace(/[^a-z]/g, '_'), 'beratung')} className="inline-flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-teal-900">
                      {item.linkLabel} <i className="ri-arrow-right-line"></i>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <a href="/hotels/buchungstool-vergleich" onClick={() => trackHotelCta('hotel_vergleich_ansehen', 'beratung')} className="inline-flex items-center gap-2 px-6 py-3 font-bold rounded-xl border-2 border-teal-500 text-teal-700 hover:bg-teal-50">
              <i className="ri-scales-3-line"></i>Zum Buchungstool-Vergleich
            </a>
          </div>
        </div>
      </section>

      {/* PROCESS STRIP */}
      <section className="py-12 sm:py-16 bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { num: '01', title: 'Erstgespräch', text: 'Portal-Anteil, Buchungstool, Zielgruppe. Sie bekommen eine Rechnung – nicht eine Floskel.' },
              { num: '02', title: 'Konzept', text: 'Struktur, Tonalität, Buchungsstrecke. Festpreis-Angebot.' },
              { num: '03', title: 'Bau', text: 'React + Buchungstool nativ eingebunden. Mobile-first. Sauberer Code, Quellcode-Übergabe.' },
              { num: '04', title: 'Übergabe', text: 'Pflege-Anleitung, Search Console eingerichtet, Wartung optional.' },
            ].map((item, i) => (
              <div key={i}>
                <div className="text-2xl font-black mb-2" style={{ background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFamily: "'Outfit', sans-serif" }}>{item.num}</div>
                <h3 className="font-bold text-gray-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                <p className="text-sm text-gray-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PAKETE */}
      <section id="pakete" className="py-16 sm:py-24" style={{ background: '#0e0e0e' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-teal-400 mb-4">Pakete und Lieferzeiten</p>
          <h2 className="text-3xl sm:text-5xl font-black text-white mb-12" style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}>
            Klassik, Premium oder<br />
            <em className="not-italic" style={{ color: '#14b8a6' }}>Marken-Auftritt.</em>
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8 flex flex-col">
              <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>KLASSIK</h3>
              <p className="text-sm text-gray-400 mb-6 leading-relaxed">Für Hotels und Pensionen, die zügig sichtbar werden wollen. Auch geeignet als erste eigene Website.</p>
              <ul className="space-y-2 text-sm text-gray-300 mb-8 flex-1">
                {['5 Seiten', 'Sprache: Deutsch', 'Buchungstool-Integration oder Anfragestrecke', 'Mobile-first', 'Basis-SEO', '1 Korrekturschleife', 'Lieferzeit: 3 Wochen', 'Zahlung: 50/50'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2"><i className="ri-check-line text-teal-400 flex-shrink-0 mt-0.5"></i><span>{item}</span></li>
                ))}
              </ul>
              <button onClick={() => goToBooking('hotel_paket_klassik', 'pakete')} className="w-full px-6 py-3 font-bold rounded-xl border-2 border-teal-500 text-teal-300 hover:bg-teal-500/10 transition-all">Klassik anfragen</button>
            </div>

            <div className="bg-white/5 rounded-2xl border-2 p-6 sm:p-8 flex flex-col shadow-2xl relative transform md:scale-105" style={{ borderColor: '#14b8a6' }}>
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white whitespace-nowrap" style={{ background: GRADIENT }}>EMPFEHLUNG</div>
              <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>PREMIUM</h3>
              <p className="text-sm text-gray-400 mb-6 leading-relaxed">Mit eigenen Werbetexten – statt austauschbarer Floskeln eine eigene Markenstimme.</p>
              <ul className="space-y-2 text-sm text-gray-300 mb-8 flex-1">
                {['8 bis 10 Seiten', 'Deutsch + Englisch', 'Buchungstool mit erweiterter mobiler Optimierung', 'Vollständige Werbetexte', 'Erweiterte SEO', 'Ausflugsziel-Sektion mit Karte', '2 Korrekturschleifen', 'Lieferzeit: 5 Wochen', 'Zahlung: 40/40/20'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2"><i className="ri-check-line text-teal-400 flex-shrink-0 mt-0.5"></i><span>{item}</span></li>
                ))}
              </ul>
              <button onClick={() => goToBooking('hotel_paket_premium', 'pakete')} className="w-full px-6 py-3 font-bold rounded-xl text-white hover:scale-105 transition-transform" style={{ background: GRADIENT, boxShadow: '0 8px 24px rgba(20,184,166,0.3)' }}>Premium anfragen</button>
            </div>

            <div className="bg-white/5 rounded-2xl border border-white/10 p-6 sm:p-8 flex flex-col">
              <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>MARKEN-AUFTRITT</h3>
              <p className="text-sm text-gray-400 mb-6 leading-relaxed">Für Häuser, die sich neu positionieren oder ein bestehendes Markenprofil schärfen wollen.</p>
              <ul className="space-y-2 text-sm text-gray-300 mb-8 flex-1">
                {['Alles aus Premium', 'Deutsch + Englisch + 3. Sprache', 'Foto-Briefing und Bildauswahl', 'Bildwelt- und Tonalitätskonzept', 'Optional: Logo-Refresh', '3 Korrekturschleifen', 'Lieferzeit: 8 Wochen', 'Zahlung: 30/30/30/10'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2"><i className="ri-check-line text-teal-400 flex-shrink-0 mt-0.5"></i><span>{item}</span></li>
                ))}
              </ul>
              <button onClick={() => goToBooking('hotel_paket_marken', 'pakete')} className="w-full px-6 py-3 font-bold rounded-xl border-2 border-teal-500 text-teal-300 hover:bg-teal-500/10 transition-all">Marken-Auftritt anfragen</button>
            </div>
          </div>

          <div className="mt-10 bg-white/5 rounded-2xl p-6 sm:p-8 border border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex-1">
              <p className="text-xs tracking-[0.2em] uppercase text-teal-400 mb-2">Optional zu allen Paketen</p>
              <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Wartung</h3>
              <p className="text-sm text-gray-400">30 Min Änderungen / Monat · Quartals-Updates · Buchungstool-Erste-Hilfe in 24 h · Monatlich kündbar</p>
            </div>
            <button onClick={() => goToBooking('hotel_wartung', 'pakete')} className="px-6 py-3 font-bold rounded-xl text-white whitespace-nowrap hover:scale-105 transition-transform" style={{ background: GRADIENT }}>Wartung dazubuchen</button>
          </div>
        </div>
      </section>

      {/* MIKRO-SERVICES */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-teal-700 mb-4">Keine neue Website nötig?</p>
          <h2 className="text-3xl sm:text-5xl font-black text-gray-900 mb-10" style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}>
            Reparieren <em className="not-italic" style={{ background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>statt neu bauen.</em>
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Buchungstool-Audit', text: 'Prüfung Ihres Buchungstool-Setups – egal welches System.', bullets: ['Widget-Check auf allen Geräten', 'Channel-Einstellungen', 'Abbruch-Analyse'], delivery: 'PDF + 30-Min-Call · 5 Werktage', cta: 'hotel_audit', label: 'Audit bestellen' },
              { title: 'Vollintegration', text: 'Bestehende Website + sauberes Buchungs-Widget einbauen.', bullets: ['Native Integration', 'Mobile-Optimierung', 'Alle gängigen Systeme'], delivery: '2 Wochen · Festpreis', cta: 'hotel_vollintegration', label: 'Anschau-Call' },
              { title: 'Setup Fresh', text: 'Cockpit + Channel-Manager + Website-Einbindung einrichten.', bullets: ['Cockpit-Setup', 'Channel-Manager', 'Website-Anbindung'], delivery: '1 Woche nach Vertragsabschluss', cta: 'hotel_setup_fresh', label: 'Setup buchen' },
            ].map((item) => (
              <div key={item.cta} className="bg-gradient-to-br from-gray-50 to-teal-50 rounded-2xl p-6 border border-teal-100 flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">{item.text}</p>
                <ul className="space-y-2 text-sm text-gray-700 mb-4 flex-1">
                  {item.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2"><i className="ri-check-line text-teal-600 flex-shrink-0 mt-0.5"></i><span>{b}</span></li>
                  ))}
                </ul>
                <p className="text-xs text-gray-500 italic mb-4">{item.delivery}</p>
                <button onClick={() => goToBooking(item.cta, 'mikro-services')} className="w-full px-4 py-3 font-bold rounded-xl border-2 border-teal-500 text-teal-700 hover:bg-teal-50 transition-all">{item.label}</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEEP DIVE HUB - REDAKTIONELLER */}
      <section className="py-16 sm:py-24 bg-[#fafaf7]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-baseline justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-teal-700 mb-2">Magazin</p>
              <h2 className="text-3xl sm:text-5xl font-black text-gray-900" style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}>
                Tiefer einsteigen<br />ins <em className="not-italic" style={{ background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Thema Hotel-Websites.</em>
              </h2>
            </div>
            <p className="text-sm text-gray-500 max-w-xs">Vier Themen, die regelmäßig im Erstgespräch auftauchen – als ausführliche Lese-Strecke.</p>
          </div>

          {/* Featured article */}
          <a href="/hotels/buchungstool-vergleich" onClick={() => trackHotelCta('hotel_deepdive_vergleich', 'deep-dive')} className="block group mb-10 bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow">
            <div className="grid md:grid-cols-2">
              <div className="aspect-[16/10] md:aspect-auto bg-gray-200 relative overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=1200&q=70"
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 sm:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 text-xs font-bold text-teal-700 uppercase tracking-wider"><i className="ri-scales-3-line"></i>Übersicht</span>
                  <span className="text-xs text-gray-400">·</span>
                  <span className="text-xs text-gray-500">8 Min Lesezeit</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-black text-gray-900 mb-4 group-hover:text-teal-700 transition-colors" style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.15 }}>
                  Buchungstool-Vergleich für Hotels 2026
                </h3>
                <p className="text-gray-600 leading-relaxed mb-5">
                  DIRS21, Lodgify, Smoobu, Bookassist und Co. – ehrlicher Vergleich aller gängigen Tools mit Preisen, Stärken/Schwächen und Empfehlung nach Hotelgröße.
                </p>
                <span className="text-sm font-semibold text-teal-700 inline-flex items-center gap-2">
                  Vergleich öffnen <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </span>
              </div>
            </div>
          </a>

          {/* Article grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { link: '/hotels/dirs21', tag: 'Tool-Detail', tagColor: '#0f4c81', img: 'https://images.unsplash.com/photo-1551105378-78e609e1d468?w=800&q=70', title: 'DIRS21-Integration für Hotel-Websites', excerpt: 'Marktführer DACH – wie wir DIRS21 nativ in Ihre Seite einbinden, statt iframe-Trick.', cta: 'hotel_deepdive_dirs21' },
              { link: '/hotels/lodgify', tag: 'Tool-Detail', tagColor: '#047857', img: 'https://images.unsplash.com/photo-1455587734955-081b22074882?w=800&q=70', title: 'Lodgify-Websites für kleine Häuser', excerpt: 'Günstige Alternative für Pensionen und B&Bs bis 10 Zimmer. Schnelle Einrichtung, faire Lizenz.', cta: 'hotel_deepdive_lodgify' },
              { link: '/hotels/direktbuchungen', tag: 'Strategie · 6 Min', tagColor: '#b45309', img: 'https://images.unsplash.com/photo-1568084680786-a84f91d1153c?w=800&q=70', title: 'Direktbuchungen erhöhen – Portal-Provision sparen', excerpt: 'Fünf konkrete Hebel, mit denen Hotels den Direktbuchungs-Anteil systematisch erhöhen.', cta: 'hotel_deepdive_direktbuchungen' },
            ].map((article) => (
              <a key={article.cta} href={article.link} onClick={() => trackHotelCta(article.cta, 'deep-dive')} className="group block">
                <div className="aspect-[4/3] mb-4 rounded-xl overflow-hidden bg-gray-200">
                  <img src={article.img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <span className="inline-block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: article.tagColor }}>{article.tag}</span>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-700 transition-colors leading-snug" style={{ fontFamily: "'Outfit', sans-serif" }}>{article.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">{article.excerpt}</p>
                <span className="text-sm font-semibold text-teal-700 inline-flex items-center gap-1">
                  Weiterlesen <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-teal-700 mb-4">Häufige Fragen</p>
          <h2 className="text-3xl sm:text-5xl font-black text-gray-900 mb-10" style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}>
            Was Hoteliers vor Auftragsvergabe <em className="not-italic" style={{ background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>wissen wollen.</em>
          </h2>

          <div className="space-y-3">
            {[
              { q: 'Mit welchen Buchungstools arbeiten Sie?', a: 'Mit allen gängigen: DIRS21, Cultuzz, Bookassist, HotelNetSolutions, Lodgify, Smoobu, easybooking, ASA Hotel, hotline und caesar data. Wenn Sie ein anderes System nutzen, prüfen wir kostenlos die Integrierbarkeit.' },
              { q: 'Empfehlen Sie ein bestimmtes Tool?', a: 'Nicht pauschal. Welches System passt, hängt von Hausgröße, Portal-Anteil, Sprachen und Personal ab. Im Erstgespräch beraten wir ehrlich – ohne Verkaufsdruck.' },
              { q: 'Wir haben noch gar keine Website. Wo fangen wir an?', a: 'Genau da, wo Sie stehen. Im 30-Minuten-Erstgespräch klären wir, welches Paket passt, ob ein Buchungstool sinnvoll ist und in welcher Reihenfolge wir vorgehen.' },
              { q: 'Was passiert mit meiner alten Website?', a: 'Wir analysieren bestehende Inhalte, übernehmen Bestand und SEO-Substanz, leiten alte URLs sauber weiter, damit Google-Rankings erhalten bleiben.' },
              { q: 'Wie lange dauert ein Projekt wirklich?', a: 'Klassik 3 Wochen, Premium 5 Wochen, Marken-Auftritt 8 Wochen – ab Vorliegen aller Materialien.' },
              { q: 'Was kostet die laufende Pflege?', a: 'Es gibt ein optionales Wartungs-Abo; Änderungen ohne Abo rechnen wir nach Aufwand ab. Die genauen Konditionen klären wir im Erstgespräch. Hosting läuft separat (typisch 10–25 €/Monat).' },
              { q: 'Bin ich an Sie gebunden, wenn ich später wechseln will?', a: 'Nein. Die Website gehört Ihnen. Quellcode wird übergeben. Sie können jederzeit Wartung oder Anbieter wechseln.' },
              { q: 'Wie kann ich die Provisions-Ersparnis konkret berechnen?', a: 'Vereinbaren Sie ein 30-minütiges Erstgespräch. Wir rechnen anhand Ihrer aktuellen Zahlen konkret durch, ab wann sich die Investition für Ihr Haus rechnet.' },
            ].map((item, i) => (
              <button key={i} onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full text-left bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl border border-teal-100 overflow-hidden">
                <div className="p-5 sm:p-6 font-bold text-gray-900 flex items-center justify-between gap-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  <span>{item.q}</span>
                  <i className={`ri-add-line text-2xl text-teal-600 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}></i>
                </div>
                {openFaq === i && (
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-gray-700 leading-relaxed">{item.a}</div>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="py-16 sm:py-24" style={{ background: GRADIENT }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center text-white">
          <h2 className="text-3xl sm:text-5xl font-black mb-6" style={{ fontFamily: "'Outfit', sans-serif", lineHeight: 1.1 }}>30 Minuten. Ein Gespräch.<br />Eine konkrete Rechnung.</h2>
          <p className="text-base sm:text-lg mb-10 leading-relaxed max-w-2xl mx-auto opacity-95">Im Erstgespräch klären wir: Welches Buchungstool nutzen Sie oder wollen Sie nutzen? Wie ist Ihr aktueller Portal-Anteil? Welches Paket passt zu Ihrem Haus?</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button onClick={() => goToBooking('hotel_termin_kalender', 'footer')} className="px-8 py-4 font-bold text-base sm:text-lg rounded-xl bg-white text-teal-700 hover:scale-105 transition-transform shadow-xl">Termin direkt im Kalender wählen</button>
            <a href="mailto:info@seitenschmied.com" onClick={() => trackHotelCta('hotel_email', 'footer')} className="px-8 py-4 font-bold text-base sm:text-lg rounded-xl border-2 border-white text-white hover:bg-white/10 transition-all">Per E-Mail kontaktieren</a>
          </div>
        </div>
      </section>

      {/* Sticky Mobile-CTA — nur auf kleinen Screens, für schnellen Abschluss */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-50 flex gap-2 p-3 bg-white/95 backdrop-blur border-t border-gray-200" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
        <button onClick={() => goToBooking('hotel_erstgespraech', 'sticky-mobile')} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 font-bold text-sm rounded-xl text-white" style={{ background: GRADIENT }}>
          <i className="ri-calendar-line"></i>Erstgespräch
        </button>
        <a href="mailto:info@seitenschmied.com" onClick={() => trackHotelCta('hotel_email', 'sticky-mobile')} className="flex items-center justify-center gap-2 px-4 py-3 font-bold text-sm rounded-xl border-2 border-teal-500 text-teal-700">
          <i className="ri-mail-line"></i>E-Mail
        </a>
      </div>

      {showBackToTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="fixed bottom-24 right-6 md:bottom-6 w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 z-40 text-white" style={{ background: GRADIENT }}>
          <i className="ri-arrow-up-line text-xl sm:text-2xl"></i>
        </button>
      )}

      <SiteFooter />
    </div>
  );
}
